import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, ShoppingCart, X, Minus, Tag } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSuperAdminCompany } from '../contexts/SuperAdminCompanyContext';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

interface Purchase {
  id: number;
  number: string;
  clientId?: number;
  tenantId: number | null;
  client?: {
    id: number;
    name: string;
    type?: string;
  };
  categoryId?: number;
  category?: {
    id: number;
    name: string;
    description?: string;
  };
  items: PurchaseItem[];
  createdAt: string;
  updatedAt: string;
}

interface PurchaseItem {
  id: number;
  purchaseId: number;
  yarnId: number;
  qtyKg: number;
  pricePerKg: number;
  yarn: {
    id: number;
    name: string;
  };
}

interface Yarn {
  id: number;
  name: string;
  isActive: boolean;
}

interface Client {
  id: number;
  name: string;
  contact: string | null;
  type: string | null;
  isActive: boolean;
}

interface PurchaseCategory {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

interface PurchaseItemForm {
  yarnId: number;
  qtyKg: number;
  pricePerKg: number;
}

interface NonYarnPurchaseItemForm {
  yarnId: number;
  quantity: number;
  rate: number;
}

interface PurchaseForm {
  clientId: number;
  categoryId?: number;
  tenantId?: number | null;
  items: PurchaseItemForm[];
}

const Purchases: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingPurchase, setDeletingPurchase] = useState<Purchase | null>(null);
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItemForm[]>([
    { yarnId: 0, qtyKg: 0, pricePerKg: 0 }
  ]);
  const [nonYarnPurchaseItems, setNonYarnPurchaseItems] = useState<NonYarnPurchaseItemForm[]>([
    { yarnId: 0, quantity: 0, rate: 0 }
  ]);
  const [selectedClientId, setSelectedClientId] = useState<number>(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [viewingPurchase, setViewingPurchase] = useState<Purchase | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { scope } = useSuperAdminCompany();
  const showCompanyColumn = user?.role === 'SUPERADMIN' && scope.mode === 'all';
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  const { reset } = useForm<PurchaseForm>({
    defaultValues: {
      items: [{ yarnId: 0, qtyKg: 0, pricePerKg: 0 }],
    },
  });

  // Fetch purchases
  const { data: purchasesResponse, isLoading, error } = useQuery({
    queryKey: ['purchases'],
    queryFn: () => apiService.get('/purchases'),
  });

  const purchases = purchasesResponse?.purchases || [];

  // Fetch yarns for dropdown
  const { data: yarnsResponse } = useQuery({
    queryKey: ['yarns'],
    queryFn: () => apiService.get('/yarns'),
  });

  const yarns = yarnsResponse?.yarns || [];

  // Fetch clients for dropdown
  const { data: clientsResponse } = useQuery({
    queryKey: ['clients'],
    queryFn: () => apiService.get('/clients'),
  });

  const clients = clientsResponse?.clients || [];

  // Fetch purchase categories for dropdown
  const { data: categoriesResponse } = useQuery({
    queryKey: ['purchase-categories-active'],
    queryFn: () => apiService.get('/purchase-categories/active'),
  });

  const categories = categoriesResponse?.data || [];

  // Create purchase mutation
  const createMutation = useMutation({
    mutationFn: (data: PurchaseForm) => apiService.post('/purchases', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['yarn-stock'] });
      toast.success('Purchase created successfully');
      setIsModalOpen(false);
      setPurchaseItems([{ yarnId: 0, qtyKg: 0, pricePerKg: 0 }]);
      setSelectedClientId(0);
      setSelectedCategoryId(0);
      setSelectedTenantId(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create purchase');
    },
  });

  // Delete purchase mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/purchases/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['yarn-stock'] });
      toast.success('Purchase deleted successfully');
      setDeletingPurchase(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete purchase');
    },
  });

  const addItem = () => {
    setPurchaseItems([...purchaseItems, { yarnId: 0, qtyKg: 0, pricePerKg: 0 }]);
  };

  const removeItem = (index: number) => {
    if (purchaseItems.length > 1) {
      setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof PurchaseItemForm, value: number) => {
    const updatedItems = [...purchaseItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setPurchaseItems(updatedItems);
  };

  const addNonYarnItem = () => {
    setNonYarnPurchaseItems([...nonYarnPurchaseItems, { yarnId: 0, quantity: 0, rate: 0 }]);
  };

  const removeNonYarnItem = (index: number) => {
    if (nonYarnPurchaseItems.length > 1) {
      setNonYarnPurchaseItems(nonYarnPurchaseItems.filter((_, i) => i !== index));
    }
  };

  const updateNonYarnItem = (index: number, field: keyof NonYarnPurchaseItemForm, value: string | number) => {
    const updatedItems = [...nonYarnPurchaseItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setNonYarnPurchaseItems(updatedItems);
  };

  // Helper function to check if selected category is yarn purchase
  const isYarnPurchase = () => {
    const selectedCategory = categories.find((cat: PurchaseCategory) => cat.id === selectedCategoryId);
    return selectedCategory?.name?.toLowerCase().includes('yarn') || false;
  };

  const onSubmit = () => {
    if (showCompanyColumn && !selectedTenantId) {
      toast.error('Please select a company');
      return;
    }

    if (isYarnPurchase()) {
      const validItems = purchaseItems.filter(item => item.yarnId && item.qtyKg > 0 && item.pricePerKg > 0);
      
      if (validItems.length === 0) {
        toast.error('Please add at least one valid yarn item');
        return;
      }

      if (!selectedClientId) {
        toast.error('Please select a client');
        return;
      }

      createMutation.mutate({
        clientId: selectedClientId,
        categoryId: selectedCategoryId || undefined,
        items: validItems,
        tenantId: showCompanyColumn ? selectedTenantId : undefined,
      });
    } else {
      const validItems = nonYarnPurchaseItems.filter(item => item.yarnId && item.quantity > 0 && item.rate > 0);
      
      if (validItems.length === 0) {
        toast.error('Please add at least one valid item');
        return;
      }

      if (!selectedClientId) {
        toast.error('Please select a client');
        return;
      }

      // For non-yarn purchases, map to standard format
      const adaptedItems = validItems.map(item => ({
        yarnId: item.yarnId,
        qtyKg: item.quantity,
        pricePerKg: item.rate
      }));

      createMutation.mutate({
        clientId: selectedClientId,
        categoryId: selectedCategoryId || undefined,
        items: adaptedItems,
        tenantId: showCompanyColumn ? selectedTenantId : undefined,
      });
    }
  };

  const handleView = (purchase: Purchase) => {
    setViewingPurchase(purchase);
  };

  const handleDelete = (purchase: Purchase) => {
    setDeletingPurchase(purchase);
  };

  const confirmDelete = () => {
    if (deletingPurchase) {
      deleteMutation.mutate(deletingPurchase.id);
    }
  };

  const openModal = () => {
    setPurchaseItems([{ yarnId: 0, qtyKg: 0, pricePerKg: 0 }]);
    setSelectedClientId(0);
    setSelectedCategoryId(0);
    setSelectedTenantId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPurchaseItems([{ yarnId: 0, qtyKg: 0, pricePerKg: 0 }]);
    setNonYarnPurchaseItems([{ yarnId: 0, quantity: 0, rate: 0 }]);
    setSelectedClientId(0);
    setSelectedCategoryId(0);
    setSelectedTenantId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load purchases. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Floating Add Purchase button */}
      <div className="fixed top-6 right-6 z-[1002]">
        <button
          type="button"
          onClick={openModal}
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
          title="Add Purchase"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>

      {/* Purchases Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {purchases.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No purchases found. Add your first purchase to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {purchases.map((purchase: Purchase) => (
              <li key={purchase.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <ShoppingCart className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">Purchase #{purchase.number}</div>
                      <div className="text-sm text-gray-500">
                        {purchase.client ? `Client: ${purchase.client.name}${purchase.client.type ? ` (${purchase.client.type})` : ''}` : 'No client assigned'}
                      </div>
                      {purchase.category && (
                        <div className="text-sm text-gray-500">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {purchase.category.name}
                          </span>
                        </div>
                      )}
                      {showCompanyColumn && (
                        <div className="text-sm text-gray-500">
                          Company: <CompanyCell tenantId={purchase.tenantId} />
                        </div>
                      )}
                      <div className="text-sm text-gray-500">
                        {purchase.items.length} items
                      </div>
                      <div className="text-sm text-gray-500">
                        Created {new Date(purchase.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleView(purchase)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(purchase)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Purchase"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add Purchase Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Add New Purchase</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {showCompanyColumn && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Purchase Type
                  </label>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(parseInt(e.target.value))}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={0}>Select a category</option>
                    {categories.map((category: PurchaseCategory) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Client
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(parseInt(e.target.value))}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={0}>Select a client</option>
                    {clients.map((client: Client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} {client.type && `(${client.type})`}
                      </option>
                    ))}
                  </select>
                </div>



                {/* Purchase Items Section */}
                <div className="flex justify-between items-center">
                  <h4 className="text-md font-medium text-gray-700">Purchase Items</h4>
                  <button
                    type="button"
                    onClick={isYarnPurchase() ? addItem : addNonYarnItem}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </button>
                </div>

                {/* Conditional Items Table */}
                {isYarnPurchase() ? (
                  /* Yarn Purchase Items Table */
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Yarn
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity (kg)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price per kg (₹)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total (₹)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {purchaseItems.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={item.yarnId}
                                onChange={(e) => updateItem(index, 'yarnId', parseInt(e.target.value))}
                                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value={0}>Select a yarn</option>
                                {yarns.map((yarn: Yarn) => (
                                  <option key={yarn.id} value={yarn.id}>
                                    {yarn.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={item.qtyKg || ''}
                                onChange={(e) => updateItem(index, 'qtyKg', parseFloat(e.target.value) || 0)}
                                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={item.pricePerKg || ''}
                                onChange={(e) => updateItem(index, 'pricePerKg', parseFloat(e.target.value) || 0)}
                                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{(item.qtyKg * item.pricePerKg).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {purchaseItems.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Non-Yarn Purchase Items Table */
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Yarn
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity (kg)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price per kg (₹)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total (₹)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {nonYarnPurchaseItems.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={item.yarnId || 0}
                                onChange={(e) => updateNonYarnItem(index, 'yarnId', parseInt(e.target.value))}
                                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value={0}>Select a yarn</option>
                                {yarns.map((yarn: Yarn) => (
                                  <option key={yarn.id} value={yarn.id}>
                                    {yarn.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={item.quantity || ''}
                                onChange={(e) => updateNonYarnItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={item.rate || ''}
                                onChange={(e) => updateNonYarnItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{(item.quantity * item.rate).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {nonYarnPurchaseItems.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeNonYarnItem(index)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-end">
                  <div className="text-lg font-semibold text-gray-900">
                    Total: ₹{isYarnPurchase() 
                      ? purchaseItems.reduce((sum, item) => sum + (item.qtyKg * item.pricePerKg), 0).toFixed(2)
                      : nonYarnPurchaseItems.reduce((sum, item) => sum + (item.quantity * item.rate), 0).toFixed(2)
                    }
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={createMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Saving...' : 'Save Purchase'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingPurchase && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Purchase</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete Purchase #{deletingPurchase.number}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingPurchase(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Purchase Details Modal */}
      {viewingPurchase && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Purchase Details - #{viewingPurchase.number}
                </h3>
                <button
                  onClick={() => setViewingPurchase(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Purchase Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Purchase Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Purchase Number:</span>
                      <p className="text-sm text-gray-900">{viewingPurchase.number}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Client:</span>
                      <p className="text-sm text-gray-900">
                        {viewingPurchase.client ?
                          `${viewingPurchase.client.name}${viewingPurchase.client.type ? ` (${viewingPurchase.client.type})` : ''}` :
                          'No client assigned'
                        }
                      </p>
                    </div>
                    {viewingPurchase.category && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Category:</span>
                        <p className="text-sm text-gray-900">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {viewingPurchase.category.name}
                          </span>
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-medium text-gray-500">Created Date:</span>
                      <p className="text-sm text-gray-900">
                        {new Date(viewingPurchase.createdAt).toLocaleDateString()} at {new Date(viewingPurchase.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total Items:</span>
                      <p className="text-sm text-gray-900">{viewingPurchase.items.length} item(s)</p>
                    </div>
                  </div>
                </div>

                {/* Purchase Items Table */}
                <div>
                  <h4 className="text-md font-medium text-gray-700 mb-3">Purchase Items</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Yarn
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity (kg)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price per kg (₹)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total (₹)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {viewingPurchase.items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.yarn.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.qtyKg}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{item.pricePerKg.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              ₹{(item.qtyKg * item.pricePerKg).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-end">
                  <div className="text-lg font-semibold text-gray-900">
                    Grand Total: ₹{viewingPurchase.items.reduce((sum, item) => sum + (item.qtyKg * item.pricePerKg), 0).toFixed(2)}
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setViewingPurchase(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchases;